﻿import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid2 from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { createStyles, makeStyles } from "@mui/styles";
import AddBoxIcon from "@mui/icons-material/AddBox";
import SearchIcon from "@mui/icons-material/Search";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowNode,
	SelectionChangedEvent,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridTooltip } from "@/components/ag-grid-tooltip";
import {
	getAssemblyTooltip,
	rowIsInAssembly,
} from "../AssembliesPanel/assemblyAnnotation";
import { AgGridReact } from "ag-grid-react";
import { debounce } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomLoadingOverlay from "../../../../../components/loaders/LazyLoading/LazyLoading";
import { useAssetDetailsContext } from "../../../context/assetDetailsContext";
import { addLinkAssetPart } from "../../../repositories/AssetDetailsRepositorie";
import { useInitialValues } from "../../hooks/useInitialValues";
import { useSearchParamsToReducer } from "../../hooks/useSearchParamsToReducer";
import { PartAssetNameColumn } from "./components/editing-row/part-AssetName.column";
import { useAssetPartsGridActionsAndButtons } from "./hooks/useAssetPartsGridActionsAndButtons";
import { useSearchAssetParts } from "./hooks/useSearchAssetParts";
import { useSearchParts } from "./hooks/useSearchParts";
import { ErrorModal } from "@dexteel/mesf-core";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { globalDefaultColDef, mesfColors } from "@setup/ag-grid/mesf-theme";
import { PropertiesModal } from "./components/properties-modal/properties-modal";
import { PartsCommentsColumn } from "./components/comments-buttons/comments-buttons.columns";
import { PartNameColumn } from "./components/editing-row/part-name.column";
import { PartDescriptionColumn } from "./components/editing-row/part-description.column";
import { PartBinNameColumn } from "./components/editing-row/part-binName.column";
import { PartsPicker } from "./components/parts-picker/parts-picker";
import { Button } from "@mui/material";
import { PartQuantityColumn } from "./components/editing-row/part-quantity.column";
import { AssetPart } from "@/pages/asset-details/models/AssetPart";

const useStyles = makeStyles((theme) =>
	createStyles({
		agGridContainer: {
			position: "relative",
			display: "flex",
			flexDirection: "column",
			height: "100%",
		},
		searchToolbar: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1),
			padding: "8px 0px",
		},
		searchField: {
			flex: 1,
			marginRight: 0,
			"& .MuiOutlinedInput-root": {
				height: "40px",
			},
		},
		addButton: {
			minWidth: "40px",
			height: "40px",
			padding: 0,
		},
		gridWrapper: {
			flex: 1,
			minHeight: 0,
		},
	}),
);

type Props = {
	searchStatistics: Function;
};

export const AssetPartsGrid = ({ searchStatistics }: Props) => {
	const navigate = useNavigate();
	const classes = useStyles();
	const [openPropertiesDialog, setOpenPropertiesDialog] =
		useState<boolean>(false);
	const [openPartsPicker, setOpenPartsPicker] = useState<boolean>(false);
	const [searchParams] = useSearchParams();
	const resource = searchParams.get("resource") ?? "";
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const [errorApi, setErrorApi] = useState("");
	const [IsAssetPartsLoading, setIsAssetPartsLoading] =
		useState<boolean>(false);
	const [assetPartsError, setAssetPartsError] = useState<string>("");
	const [linkPartError, setLinkPartError] = useState<string>("");
	const [removePartError, setRemovePartError] = useState<string>("");
	const [listAllParts, setListAllParts] = useState<AssetPart[]>([]);
	const [quickFilterText, setQuickFilterText] = useState("");
	const [includeDescendants, setIncludeDescendants] = useState(true);

	const {
		state: { assetSearchId, listAssetParts, partSearch, rowSelected },
		actions: {
			setFirstTime,
			setShowListOfParts,
			setPartSearch,
			setRowSelected,
			setLoaderListSearchParts,
		},
	} = useAssetDetailsContext();

	const debouncedSearch = useRef(
		debounce((partSearch?: string) => {
			searchParts(partSearch || undefined).then(() => {
				setLoaderListSearchParts(false);
			});
		}, 3000),
	).current;

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const containerStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [listPartSelected, setListPartSelected] = useState<any[]>([]);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>();

	const searchAssetParts = useSearchAssetParts({
		setIsAssetPartsLoading,
		setAssetPartsError,
	});

	const searchParts = useSearchParts({
		setIsLoading,
		setError,
		setListAllParts,
	});

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const {
		refresh,
		removeSelected,
		saveButton,
		deleteOpen,
		setSaveButton,
		setDeleteOpen,
	} = useAssetPartsGridActionsAndButtons({
		gridRef: gridApi,
		searchStatistics,
		searchAssetParts,
		setRemovePartError,
		includeDescendants,
	});

	const openPartPicker = useCallback(() => {
		searchParts();
		setOpenPartsPicker(true);
	}, [searchParts]);

	const handlePartPickerSave = useCallback(
		(shouldUpdate: boolean) => {
			setOpenPartsPicker(false);
			if (shouldUpdate) {
				refresh();
			}
		},
		[refresh],
	);

	const handleQuickFilterChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			setQuickFilterText(value);
			if (gridApi) {
				gridApi.setGridOption("quickFilterText", value);
			}
		},
		[gridApi],
	);

	const renderColumns = () => {
		setColumnDefs([]);
		const newColumns: ColDef[] = [];
		newColumns.push({
			field: "",
			flex: 1,
			minWidth: 30,
			maxWidth: 30,
			filter: false,
			checkboxSelection: true,
			headerCheckboxSelection: true,
			headerCheckboxSelectionFilteredOnly: true,
			getQuickFilterText: () => "",
		});
		newColumns.push({
			cellRenderer: PartNameColumn,
			type: "columnProperties",
			field: "Name",
			headerName: "Part",
			flex: 2,
			minWidth: 150,
			maxWidth: 150,
			getQuickFilterText: (params) => params.data?.Name || "",
		});
		newColumns.push({
			cellRenderer: PartDescriptionColumn,
			type: "columnProperties",
			field: "Description",
			headerName: "Description",
			flex: 4,
			minWidth: 290,
			getQuickFilterText: (params) => params.data?.Description || "",
		});
		newColumns.push({
			cellRenderer: PartAssetNameColumn,
			type: "columnProperties",
			field: "AssetName",
			headerName: "Asset",
			flex: 1,
			minWidth: 220,
			maxWidth: 220,
			getQuickFilterText: (params) => {
				const assetName = params.data?.AssetName || params.data?.FullPath || "";
				return assetName;
			},
		});

		newColumns.push({
			cellRenderer: PartBinNameColumn,
			type: "columnProperties",
			field: "BinName",
			headerName: "BinName",
			flex: 1,
			minWidth: 80,
			maxWidth: 80,
			getQuickFilterText: (params) => params.data?.BinName || "",
		});
		newColumns.push({
			cellRenderer: PartQuantityColumn,
			type: "columnProperties",
			field: "Quantity",
			headerName: "Quantity",
			flex: 1,
			minWidth: 80,
			maxWidth: 80,
			getQuickFilterText: (params) => params.data?.Quantity || "",
		});
		newColumns.push({
			cellRenderer: (params: any) =>
				PartsCommentsColumn({
					data: params?.data,
					searchAssetParts,
				}),
			field: "Comments",
			flex: 1,
			type: "dataName",
			minWidth: 90,
			getQuickFilterText: (params) => params.data?.Comments || "",
		});
		newColumns.push({
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid2
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid2
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
							size={{ xs: 12, md: 12 }}
						>
							<Grid2
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid2 size={{ xs: 12, md: 12 }}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				);
			},
		});
		setColumnDefs(newColumns);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			if (gridApi && params.node) {
				const selectedNodes = gridApi.getSelectedNodes();
				const isCurrentlySelected = params.node.isSelected();

				if (selectedNodes.length > 1 && isCurrentlySelected) {
				} else if (!isCurrentlySelected) {
					gridApi.deselectAll();
					params.node.setSelected(true);
				}
			}

			setRowSelected(params.node?.data);
			const rowSelectedInGrid = params.node?.data;
			const selectedParts = gridApi?.getSelectedNodes();

			let drawginsLinksToP = null;
			try {
				const partCopiedData = localStorage.getItem("partCopied");
				if (partCopiedData) {
					drawginsLinksToP = JSON.parse(partCopiedData);
				}
			} catch (error) {
				console.error("Error parsing partCopied from localStorage:", error);
				localStorage.removeItem("partCopied");
			}

			const PartsCopiedInLocalStorage = () => {
				try {
					if (selectedParts?.length) {
						const selectedPartsData = selectedParts.map(
							(rowNode) => rowNode?.data,
						);
						localStorage.setItem(
							"partCopied",
							JSON.stringify(selectedPartsData),
						);
					} else if (rowSelectedInGrid) {
						localStorage.setItem(
							"partCopied",
							JSON.stringify([rowSelectedInGrid]),
						);
					}
				} catch (error) {
					console.error("Error saving parts to localStorage:", error);
					setError("Error copying parts");
				}
			};

			const PastePartsCopiedInLocalStorage = async () => {
				try {
					let partNames: string[] = [];

					// Collect all part names
					if (Array.isArray(drawginsLinksToP)) {
						partNames = drawginsLinksToP
							.map((item) => item.Name)
							.filter((name) => name);
					} else if (typeof drawginsLinksToP === "string") {
						partNames = [drawginsLinksToP];
					}

					if (partNames.length === 0) {
						setError("No parts to paste");
						return;
					}

					// Single bulk API call
					const bulkResult = await addLinkAssetPart(
						assetSearchId as string,
						partNames, // Pass array directly, function will handle it
						"",
					);

					if (!bulkResult.ok) {
						setLinkPartError(bulkResult.message);
						return;
					}

					// Parse results
					const result = bulkResult.data?.tables?.[0]?.rows?.[0];
					if (result) {
						const newlyLinkedCount = result.NewlyLinkedCount || 0;
						const alreadyLinkedCount = result.AlreadyLinkedCount || 0;
						const alreadyLinkedParts = result.AlreadyLinkedParts;

						// Show summary message
						let message = `Successfully processed ${partNames.length} parts:\n`;
						message += `• ${newlyLinkedCount} newly linked\n`;
						if (alreadyLinkedCount > 0) {
							message += `• ${alreadyLinkedCount} already linked: ${alreadyLinkedParts}`;
						}

						if (alreadyLinkedCount > 0) {
							setError(message); // Show as warning for user awareness
						}
					}

					refresh();
				} catch (error) {
					console.error("Error pasting parts:", error);
					setError("Error pasting parts");
				}
			};

			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (
				rowSelectedInGrid &&
				rowSelectedInGrid?.id !== -1 &&
				(selectedParts?.length ?? 0) <= 1
			) {
				result.push({
					name: "Properties",
					action: () => {
						setOpenPropertiesDialog(true);
						setRowSelected(params.node?.data);
					},
					icon: '<i class="fas fa-file-invoice"></i>',
				});
			}

			if (
				rowSelectedInGrid &&
				rowSelectedInGrid?.id !== -1 &&
				(selectedParts?.length ?? 0) <= 1
			) {
				result.push({
					name: "Go to Asset",
					action: () => {
						setFirstTime(true);
						setSaveButton(false);
						setDeleteOpen(false);
						setPartSearch("");
						setTimeout(() => {
							const currentResource = searchParams.get("resource") || "parts";
							const currentSearchParams = new URLSearchParams(searchParams);
							currentSearchParams.set("resource", currentResource);

							navigate(
								`/asset-details/${params.node?.data.AssetId}?${currentSearchParams.toString()}`,
								{
									replace: true,
								},
							);
							setIsAssetPartsLoading(false);
						}, 100);
					},
					icon: '<i class="fas fa-external-link-alt"></i>',
				});
			}

			if (selectedParts?.length || rowSelectedInGrid) {
				result.push({
					name:
						(selectedParts?.length ?? 0) > 1
							? "Copy Part Links"
							: "Copy Part Link",
					action: PartsCopiedInLocalStorage,
					icon: '<i class="far fa-copy"></i>',
				});
			}

			if (
				drawginsLinksToP &&
				((Array.isArray(drawginsLinksToP) && drawginsLinksToP.length > 0) ||
					typeof drawginsLinksToP === "string") &&
				(selectedParts?.length ?? 0) <= 1
			) {
				result.push({
					name: "Paste Part Link",
					action: PastePartsCopiedInLocalStorage,
					icon: '<i class="fas fa-paste"></i>',
				});
			}

			if ((selectedParts?.length ?? 0) <= 1) {
				result.push({
					name: "Add Part Link",
					action: openPartPicker,
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			if (selectedParts?.length) {
				// A part can only be unlinked from the asset it belongs to; disable Remove (with an
				// explaining tooltip) when any selected part is linked to another asset, so the user
				// never triggers the "cannot remove parts linked to other assets" error.
				const allOwned = selectedParts.every(
					(n) => Number(n.data?.AssetId) === Number(assetSearchId),
				);
				result.push({
					name:
						(selectedParts?.length ?? 0) > 1
							? "Remove Part Links"
							: "Remove Part Link",
					disabled: !allOwned,
					tooltip: allOwned
						? undefined
						: 'Go to the part\'s asset ("Go to Asset") to remove it',
					action: () => {
						if (!allOwned) return;
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			if (
				!selectedParts?.length &&
				rowSelectedInGrid?.id !== -1 &&
				rowSelectedInGrid
			) {
				const owned =
					Number(rowSelectedInGrid.AssetId) === Number(assetSearchId);
				result.push({
					name: "Remove Part Link",
					disabled: !owned,
					tooltip: owned
						? undefined
						: 'Go to the part\'s asset ("Go to Asset") to remove it',
					action: () => {
						if (!owned) return;
						setRowSelected(params.node?.data);
						setListPartSelected([]);
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			return result;
		},
		[
			listAssetParts,
			saveButton,
			openPartPicker,
			assetSearchId,
			gridApi,
			rowSelected,
			searchParams,
			refresh,
		],
	);

	const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
		const rowCount = event.api.getSelectedNodes().length;
		const listPartsSelected = event.api.getSelectedNodes();
		setListPartSelected(listPartsSelected);
	}, []);

	const openModalViewerActive = () => {
		const selectedRow: RowNode<any>[] | any = gridApi?.getSelectedNodes();
		if (selectedRow[0]!.data.AssetName !== undefined) {
			setRowSelected(selectedRow[0]!.data);
			setOpenPropertiesDialog(!openPropertiesDialog);
		}
	};

	useEffect(() => {
		if (partSearch.length >= 5) {
			setLoaderListSearchParts(true);
			setShowListOfParts(true);
			debouncedSearch(partSearch);
		} else {
			setShowListOfParts(false);
		}
	}, [partSearch]);

	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	useInitialValues();
	useSearchParamsToReducer();

	useEffect(() => {
		setSaveButton(false);
		setPartSearch("");
	}, [assetSearchId]);

	useEffect(() => {
		renderColumns();
	}, []);

	useEffect(() => {
		// Only search if we have a valid assetSearchId (not 0, undefined, or null)
		// This prevents the initial call with assetSearchId=0 when the component first mounts
		if (resource === "parts" && assetSearchId && assetSearchId !== 0) {
			searchAssetParts(includeDescendants);
		}
	}, [resource, assetSearchId, includeDescendants]);

	return (
		<>
			<div style={containerStyle}>
				<Grid2 container style={gridStyle}>
					<Grid2 className={classes.agGridContainer} size={{ md: 12, xs: 12 }}>
						<div className={classes.searchToolbar}>
							<TextField
								className={classes.searchField}
								variant="outlined"
								placeholder="Search parts..."
								value={quickFilterText}
								onChange={handleQuickFilterChange}
								slotProps={{
									input: {
										startAdornment: (
											<SearchIcon color="action" style={{ marginRight: 8 }} />
										),
									},
								}}
								size="small"
							/>
							<FormControlLabel
								control={
									<Switch
										checked={includeDescendants}
										onChange={(e) => setIncludeDescendants(e.target.checked)}
										color="primary"
									/>
								}
								label="Include descendant assets"
								style={{ marginLeft: 16, marginRight: 16 }}
							/>
							<IconButton
								onClick={openPartPicker}
								aria-label="addParts"
								color="primary"
								className={classes.addButton}
							>
								<AddBoxIcon style={{ fontSize: 40, fontWeight: "bold" }} />
							</IconButton>
						</div>

						<div className={classes.gridWrapper}>
							<AgGridReact
								onGridReady={(params) => setGridApi(params.api)}
								rowData={listAssetParts}
								columnDefs={columnDefs}
								// Assembly tooltip on every cell so hovering anywhere on a tinted row shows
								// which assemblies contain the part (returns undefined otherwise).
								defaultColDef={{
									...globalDefaultColDef,
									tooltipComponent: "agGridTooltip",
									tooltipValueGetter: (p) => getAssemblyTooltip(p.data),
								}}
								components={{ agGridTooltip: AgGridTooltip }}
								tooltipShowDelay={300}
								getRowStyle={(params) =>
									rowIsInAssembly(params.data)
										? { background: mesfColors.resource.parts }
										: undefined
								}
								headerHeight={32}
								rowSelection={"multiple"}
								animateRows={true}
								getContextMenuItems={getContextMenuItems}
								onSelectionChanged={onSelectionChanged}
								loadingOverlayComponent={loadingOverlayComponent}
								onRowDoubleClicked={openModalViewerActive}
								loading={IsAssetPartsLoading}
								quickFilterText={quickFilterText}
							/>
						</div>

						<PartsPicker
							searchAssetParts={searchAssetParts}
							show={openPartsPicker}
							listAllParts={listAllParts}
							isLoading={isLoading}
							onHide={handlePartPickerSave}
							AssetId={assetSearchId as number}
							onCatalogReloaded={setListAllParts}
						/>
					</Grid2>
					<Dialog open={deleteOpen} aria-labelledby="responsive-dialog-title">
						<DialogTitle id="responsive-dialog-title">{"WARNING"}</DialogTitle>
						<DialogContent>
							<DialogContentText style={{ padding: 0, margin: 0 }}>
								{listPartSelected?.length === 1 ? (
									<>
										<p style={{ margin: "0" }}>
											UNLINK <b>{listPartSelected[0].data.Name}</b> FROM ASSET{" "}
											<b>{listPartSelected[0].data.AssetName}?</b>
										</p>
									</>
								) : listPartSelected.length > 1 ? (
									<>
										<p style={{ margin: 0 }}>UNLINK:</p>
										{listPartSelected?.map((part) => {
											return (
												<>
													<p style={{ margin: "5px 0" }}>
														<b>• {part.data.Name}, </b>
													</p>
													<p style={{ margin: 0 }}>
														FROM ASSET <b>{part.data.AssetName}.</b>
													</p>
												</>
											);
										})}
									</>
								) : rowSelected?.Name && listPartSelected?.length === 0 ? (
									<>
										<p style={{ margin: "0" }}>
											UNLINK <b>{rowSelected?.Name}</b> FROM ASSET{" "}
											<b>{rowSelected?.AssetName}?</b>
										</p>
									</>
								) : (
									<>
										<h1>Something went wrong</h1>
									</>
								)}
							</DialogContentText>
						</DialogContent>
						<DialogActions style={{ padding: "16px 24px" }}>
							<Button
								onClick={() => setDeleteOpen(false)}
								autoFocus
								color="inherit"
							>
								CANCEL
							</Button>
							<Button autoFocus color="secondary" onClick={removeSelected}>
								UNLINK
							</Button>
						</DialogActions>
					</Dialog>
				</Grid2>
				<PropertiesModal
					searchAssetParts={searchAssetParts}
					show={openPropertiesDialog}
					rowSelected={rowSelected as any}
					onHide={(shouldUpdate) => {
						if (shouldUpdate) {
							setOpenPropertiesDialog(false);
							searchAssetParts(includeDescendants);
						} else {
							setOpenPropertiesDialog(false);
						}
					}}
				/>
				<ErrorModal error={error} onHide={() => setError("")} />
				<ErrorModal error={errorApi} onHide={() => setErrorApi("")} />
				<ErrorModal
					error={assetPartsError}
					onHide={() => setAssetPartsError("")}
				/>
				<ErrorModal error={linkPartError} onHide={() => setLinkPartError("")} />
				<ErrorModal
					error={removePartError}
					onHide={() => setRemovePartError("")}
				/>
			</div>
		</>
	);
};
